<template>
  <q-page
    :data-test="CypressTarget('MyAddresses')"
    padding
  >
    <q-item
      class="col-12"
    >
      <q-card
        :data-test="CypressTarget('ProductGroup')"
        :class="{ 'bg-white': true }"
        class="full-width myAddress-group"
      >
        <q-card-title
          :data-test="CypressTarget('Title')"
          :style="{ 'padding-bottom': '0px'}"
        >
          <div class="cart-event-group-header-cols">
            <div
              :class="{'cart-event-group-header-col-a': true}"
              :style="{'font-size': '12pt'}"
            >
              {{ $t('label.myAddresses') }}
            </div>
          </div>
        </q-card-title>
        <q-card-main
          :data-test="CypressTarget('MyAddresses')"
          class="no-padding"
        >
          <q-item v-if="myAddresses.length === 0">
            <span class="q-subheading q-mx-auto text-grey">{{ $t('label.delivery.noAddressFound') }}...</span>
          </q-item>
          <q-item
            v-for="address in myAddresses"
            v-else
            :key="address.id"
            class="myAddress"
          >
            <q-item-main>
              <div>
                <b class="q-ml-sm">{{ address.alias }}</b>
              </div>
              <div class="q-ml-sm">
                {{ address.address }},
                {{ address.address_number }},
                {{ address.complement ? `${address.complement}, ` : '' }}
                {{ address.city }} -
                {{ address.state }}
              </div>
            </q-item-main>
            <q-item-side right>
              <q-btn
                flat
                color="primary"
                rounded
                size="sm"
                :title="$t('label.delivery.addressEditTitle')"
                @click="showAlert(MODAL_TYPE.EDIT_ADDRESS, address)"
              >
                <i
                  class="fa fa-edit"
                  style="font-size: 1.2em !important"
                />
              </q-btn>
              <q-btn
                flat
                color="primary"
                rounded
                size="sm"
                :title="$t('label.delivery.addressDeleteTitle')"
                @click="showAlert(MODAL_TYPE.DELETE_ADDRESS, address)"
              >
                <i
                  class="fa fa-trash"
                  style="font-size: 1.2em !important"
                />
              </q-btn>
            </q-item-side>
          </q-item>
        </q-card-main>
      </q-card>
    </q-item>
    <q-item>
      <q-item-main class="text-center">
        <q-btn
          v-if="myAddresses.length < MAX_ADDRESS_COUNT"
          :data-test="CypressTarget('NewAddress')"
          :label="$t('button.newAddress')"
          color="primary"
          rounded
          flat
          class="q-mr-none"
          @click="editAddress(null)"
        />
        <span
          v-else
          class="text-grey"
        >
          {{ $t('text.checkout.delivery.maxAddress', { qty: MAX_ADDRESS_COUNT }) }}
        </span>
      </q-item-main>
    </q-item>
    <q-modal
      v-model="show_alert"
      minimized
      content-classes="q-pa-md"
      content-css="max-width: 600px"
    >
      <div class="row justify-center exclamation text-red-12">
        <i class="fa fa-exclamation-triangle fa-4x padding-bottom" />
        {{ $t('label.attention') }}
      </div>
      <div class="row justify-center padding-bottom">
        {{ $t('dialog.myAddresses.alert') }}
      </div>
      <div class="row justify-center">
        <q-btn
          flat
          :label="$t('button.cancel')"
          @click="show_alert = false"
        />
        <q-btn
          color="primary"
          :label="$t('button.agreed')"
          @click="callModal()"
        />
      </div>
    </q-modal>
    <q-modal
      v-model="show_address_dialog"
      minimized
      content-classes="q-pa-md"
      content-css="max-width: 600px"
    >
      <div
        class="q-title q-mb-md"
      >
        {{ editing_address.id !== -1 ? editing_address.alias : $t('button.newAddress') }}
      </div>
      <div class="q-mb-md">
        <q-field
          :data-test="CypressTarget('AddressName')"
          :error="$v.editing_address.alias.$error"
          :error-label="$t('error.required.field', {field: $t('field.delivery.alias')})"
        >
          <q-input
            id="addressName"
            ref="addressName"
            v-model="editing_address.alias"
            :data-test="CypressTarget('Input')"
            :float-label="$t('field.delivery.alias')"
            :color="SET_COLOR_BUTTON.primary"
            :text-color="SET_COLOR_BUTTON.text.primary"
            type="text"
            maxlength="255"
          />
        </q-field>
      </div>
      <div>
        <q-field
          v-if="editing_address.id === -1 && getUserAddress.country === BRAZIL"
          :data-test="CypressTarget('useBuyerAddress')"
          class="q-mb-sm"
        >
          <q-checkbox
            id="useBuyerAddress"
            ref="useBuyerAddress"
            v-model="use_buyer_address"
            :data-test="CypressTarget('Input')"
            :label="$t('text.myAddresses.useBuyerAddress')"
            :color="SET_COLOR.primary"
          />
        </q-field>
      </div>
      <div>
        <brazilian-address-form
          :key="address_key"
          ref="addressForm"
          :value="editing_address"
          :can-edit="!use_buyer_address"
          :fields-settings="{ ...address_settings }"
          lock-country
          @input="editing_address = $event"
        />
      </div>
      <div class="row justify-end">
        <q-btn
          flat
          :label="$t('button.cancel')"
          @click="cancelAddressEdit()"
        />
        <q-btn
          color="primary"
          :label="$t('button.save')"
          @click="save"
        />
      </div>
      <q-inner-loading
        :visible="saving_address"
        color="primary"
      />
    </q-modal>
    <q-inner-loading
      :visible="saving_address || saving"
      color="primary"
    />
  </q-page>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { BRAZIL } from 'constants/countries';
import { userDataDefault } from 'store/modules/user/state';
import { required } from 'vuelidate/lib/validators';

const newAddress = {
  ...userDataDefault.address,
  alias: '',
  id: -1,
  zipcode: '',
  favorite: false,
  country: 'BR',
};

const addressSettings = {
  address: { isRequired: true, isEnabled: true },
  zipcode: { isRequired: true, isEnabled: true },
  address2: { isRequired: false, isEnabled: true },
  address_number: { isRequired: true, isEnabled: true },
  district: { isRequired: true, isEnabled: true },
  city: { isRequired: true, isEnabled: true },
  state: { isRequired: true, isEnabled: true },
  country: { isRequired: true, isEnabled: true },
};

export default {
  name: 'MyAddressesPage',
  components: {
    brazilianAddressForm: () => import('components/form/address/brazilian'),
  },
  data: () => ({
    BRAZIL,
    selected_address: null,
    formAddress: { zipcode: '' },
    show_address_dialog: false,
    show_alert: false,
    editing_address: newAddress,
    use_buyer_address: false,
    saving_address: false,
    MAX_ADDRESS_COUNT: 5,
    saving: false,
    address_settings: addressSettings,
    address_key: 0,
    modal_selected: false,
    MODAL_TYPE: {
      EDIT_ADDRESS: 'editAddress',
      DELETE_ADDRESS: 'deleteAddress',
    },
  }),
  computed: {
    ...mapGetters('user', [
      'myAddresses',
    ]),
  },
  async beforeMount() {
    this.loadMyAddresses();
  },
  methods: {
    ...mapActions('user', [
      'getUserAddress',
      'loadMyAddresses',
      'saveMyAddress',
      'deleteMyAddress',
    ]),
    validateForm() {
      if (!this.$refs.addressForm) return false;
      this.$v.editing_address.alias.$touch();
      this.$refs.addressForm.touchForm();
      return !this.$refs.addressForm.validateForm() && !this.$v.editing_address.$invalid;
    },
    showAlert(type, address) {
      this.selected_address = address;
      this.modal_selected = type;
      this.show_alert = true;
    },
    callModal() {
      this.show_alert = false;
      if (this.modal_selected === this.MODAL_TYPE.EDIT_ADDRESS) {
        this.editAddress(this.selected_address);
      } else {
        this.remove(this.selected_address);
      }
    },
    editAddress(address) {
      this.use_buyer_address = false;
      this.$v.editing_address.alias.$reset();
      this.editing_address = {
        ...newAddress,
        ...(address || {}),
        address_number: address ? address.address_number : '',
        address2: address ? address.complement : '',
      };
      this.show_address_dialog = true;
      this.address_key += 1;
    },
    async save() {
      if (!this.validateForm()) {
        return false;
      }
      try {
        this.saving_address = true;
        const isNewAddress = this.editing_address.id === -1;
        await this.saveMyAddress(this.editing_address);
        this.show_address_dialog = false;
        this.selected_address = false;
        this.show_alert = false;
        this.$q.notify({
          message: this.$t((isNewAddress ? 'dialog.myAddresses.new' : 'dialog.myAddresses.edit')),
          timeout: 3000,
          color: 'positive',
          icon: 'check',
        });
        return true;
      } catch (error) {
        this.toastError(this.$t('text.myAddresses.errorOnSave'));
        return false;
      } finally {
        this.saving_address = false;
      }
    },
    async remove(address) {
      try {
        this.saving_address = true;
        await this.deleteMyAddress(address);
        this.selected_address = false;
        this.show_alert = false;
        this.$q.notify({
          message: this.$t('dialog.myAddresses.delete'),
          timeout: 3000,
          color: 'positive',
          icon: 'check',
        });
      } catch (error) {
        this.toastError(this.$t('text.myAddresses.errorOnDelete'));
      } finally {
        this.saving_address = false;
      }
    },
    cancelAddressEdit() {
      this.show_address_dialog = false;
      this.editing_address = newAddress;
      this.selected_address = false;
      this.show_alert = false;
    },
  },
  validations() {
    return ({
      editing_address: {
        alias: { required },
      },
    });
  },
};
</script>

<style lang="stylus">

  .myAddress-group
    border: 1px solid #f4f4f4
    border-radius: 9px
    box-shadow : none
    padding:0 0 0 0;

  .myAddress-group
    .q-card-title
      font-weight : 700
      padding: 0 0 16px 0

  .myAddress-group .address.q-item, .myAddress
    padding: 10px 16px 16px
    margin:0!important
    background-color :#f4f4f4
  .myAddress-group .address.q-item:not(:last-child)
    border-bottom: 1px solid #dddddd

  .myAddress
    display: flex;
    flex-wrap: wrap;

  .myAddress-item:not(:last-child)
    flex: 1 1 100%;
    padding-bottom: 14px;

  .exclamation
    padding-bottom: 14px;
    display: flex;
    flex-direction: column;
    column-gap: 10px;
    text-align: center;
    font-weight: bold;

  .padding-bottom
    padding-bottom: 14px;
</style>
